import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { Button, Input, Select, Archive, CircleStop, Trash2 } from "../../ui";
import { cancelCampaignAPI, deleteCampaignRequestAPI } from "../../../utils/api/actions";
import { DELIVERY_APPS, integrationOptions, normalizeIntegrationKey } from "../../../utils/integrations";
import { getCampaignText } from "./campaignActionsText";
import CampaignModal from "./CampaignModal";
import CampaignPicker from "./CampaignPicker";
import { runBatch } from "./bulk";
import { describeError, useStoreCampaigns } from "./useStoreCampaigns";

// A store that has to be chosen first (preselected when there is only one).
function useStoreChoice(stores) {
  const [storeId, setStoreId] = useState(stores.length === 1 ? stores[0].id : "");
  useEffect(() => {
    if (stores.length === 1) setStoreId(stores[0].id);
  }, [stores]);
  const storeNames = useMemo(
    () => Object.fromEntries(stores.map((store) => [store.id, store.storeName])),
    [stores],
  );
  return { storeId, setStoreId, storeNames };
}

function StoreField({ stores, storeId, onChange, label, placeholder, disabled }) {
  return (
    <Select
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      options={stores.map((store) => ({ value: store.id, label: `${store.storeName} (#${store.id})` }))}
      value={storeId}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

// Shared body of the three store-scoped flows (cancel, delete, deleted list).
function StoreCampaignModal({
  mode,
  stores,
  onClose,
  onDone,
  title,
  icon,
  status,
  filter,
  emptyText,
  submit,
  submitLabel,
  submitLabelMany,
  warning,
  reasonLabel,
  // One application per action: the list shows nothing until one is chosen.
  singleApp = false,
}) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  const { canForAll } = useRestaurantAccess(stores);
  const { storeId, setStoreId, storeNames } = useStoreChoice(stores);
  const { items: storeItems, loading, error, reload } = useStoreCampaigns({ storeId, status, filter, storeNames });
  const [appName, setAppName] = useState("");
  const items = useMemo(
    () => (singleApp ? storeItems.filter((item) => normalizeIntegrationKey(item.appName) === appName) : storeItems),
    [singleApp, storeItems, appName],
  );
  // Older records can belong to an integration outside the usual list; they stay reachable.
  const appOptions = useMemo(() => {
    const extra = storeItems.map((item) => normalizeIntegrationKey(item.appName)).filter((key) => key && !DELIVERY_APPS.includes(key));
    return integrationOptions([...new Set([...DELIVERY_APPS, ...extra])], lang);
  }, [storeItems, lang]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitError, setSubmitError] = useState("");
  const [failures, setFailures] = useState([]);
  // A ref guards against a second click before state has re-rendered.
  const runningRef = useRef(false);

  // The selection never outlives the list it was picked from.
  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => items.some((item) => item.id === id));
      return next.length === current.length ? current : next;
    });
  }, [items]);

  const selectedItems = items.filter((item) => selectedIds.includes(item.id));

  // The API handles one campaign per request, so each selected campaign runs
  // the existing action in turn; failures are collected, not hidden.
  const handleSubmit = async () => {
    if (!selectedItems.length || runningRef.current) return;
    // handler-level authorization: the restaurant being worked on must be in scope
    if (!canForAll("operation.campaign.use", [storeId])) return;
    runningRef.current = true;
    const token = localStorage.getItem("auth-token");
    const batch = selectedItems;
    setSubmitting(true);
    setSubmitError("");
    setFailures([]);
    setProgress(0);
    const { succeeded: done, failed: errors } = await runBatch(batch, (item) => submit(token, item, reason), setProgress);
    const succeeded = done.length;
    const failed = errors.map(({ item, error }) => ({ item, message: describeError(error, text) }));
    runningRef.current = false;
    setSubmitting(false);
    if (succeeded) onDone();
    if (!failed.length) {
      onClose();
      return;
    }
    // Keep the dialog open on exactly the campaigns that failed.
    setFailures(failed);
    setSubmitError(succeeded ? text.bulkPartial(succeeded, batch.length) : text.bulkFailed);
    setSelectedIds(failed.map(({ item }) => item.id));
    reload();
  };

  const readOnly = mode === "deleted";

  return (
    <CampaignModal
      title={title}
      icon={icon}
      onClose={onClose}
      busy={submitting}
      closeLabel={text.close}
      footer={
        readOnly ? (
          <Button appearance="soft" variant="neutral" onClick={onClose}>
            {text.close}
          </Button>
        ) : (
          <>
            <Button
              variant="danger"
              appearance="filled"
              disabled={!selectedItems.length || submitting}
              loading={submitting}
              onClick={handleSubmit}
            >
              {selectedItems.length > 1 ? submitLabelMany(selectedItems.length) : submitLabel}
            </Button>
            <Button variant="neutral" appearance="soft" disabled={submitting} onClick={onClose}>
              {text.close}
            </Button>
          </>
        )
      }
    >
      <StoreField
        stores={stores}
        storeId={storeId}
        label={text.store}
        placeholder={text.selectStore}
        disabled={submitting}
        onChange={(value) => {
          setStoreId(value);
          setSelectedIds([]);
          setFailures([]);
          setSubmitError("");
        }}
      />

      {singleApp && (
        <div className="ca-grid-2">
          <Select
            label={text.app}
            placeholder={text.selectApp}
            options={appOptions}
            value={appName}
            disabled={submitting}
            onChange={(event) => {
              setAppName(event.target.value);
              setSelectedIds([]);
              setFailures([]);
              setSubmitError("");
            }}
          />
        </div>
      )}

      {!storeId ? (
        <p className="ca-picker-state">{text.chooseStoreFirst}</p>
      ) : singleApp && !appName ? (
        <p className="ca-picker-state">{text.chooseAppFirst}</p>
      ) : (
        <CampaignPicker
          items={items}
          loading={loading}
          error={error}
          onRetry={reload}
          emptyText={emptyText}
          selectedIds={readOnly ? undefined : selectedIds}
          onSelectedIdsChange={setSelectedIds}
          disabled={submitting}
        />
      )}

      {!readOnly && selectedItems.length > 0 && (
        <div className="ca-confirm">
          <p className="ca-warning">
            {warning}
          </p>
          {reasonLabel && (
            <Input
              textarea
              label={reasonLabel}
              value={reason}
              maxLength={500}
              disabled={submitting}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </div>
      )}

      {submitting && (
        <p className="ca-progress" role="status">
          {text.progress(progress, selectedItems.length)}
        </p>
      )}

      {submitError && (
        <div className="ca-error" role="alert">
          {submitError}
          {failures.length > 0 && (
            <ul className="ca-failures">
              {failures.map(({ item, message }) => (
                <li key={item.id}>
                  <strong>{item.title}</strong>: {message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </CampaignModal>
  );
}

// Only approved (in progress) requests the workflow lets this user cancel are offered
// (the query asks for them and the list is filtered by the server's `allowed` flag);
// the server checks once more when the cancellation is submitted.
const isCancellable = (action) => action.status === "inprogress" && action.allowed?.cancel === true;

export function CancelCampaignModal({ stores, onClose, onDone }) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  return (
    <StoreCampaignModal
      mode="cancel"
      singleApp
      stores={stores}
      onClose={onClose}
      onDone={onDone}
      title={text.cancelTitle}
      icon={<CircleStop size={20} aria-hidden="true" />}
      status="inprogress"
      filter={isCancellable}
      emptyText={text.noActive}
      submitLabel={text.cancelConfirm}
      submitLabelMany={text.cancelConfirmMany}
      warning={text.cancelWarning}
      reasonLabel={text.cancelReason}
      submit={(token, item, reason) => cancelCampaignAPI(token, item.id, reason.trim() || undefined)}
    />
  );
}

// New requests in the restaurant-review step that LUD may delete (the server's `allowed` flag).
const isNewRequest = (action) => action.status === "pending" && action.origin !== "alreadyActive" && action.allowed?.delete === true;

export function DeleteCampaignModal({ stores, onClose, onDone }) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  return (
    <StoreCampaignModal
      mode="delete"
      stores={stores}
      onClose={onClose}
      onDone={onDone}
      title={text.deleteTitle}
      icon={<Trash2 size={20} aria-hidden="true" />}
      status="pending"
      filter={isNewRequest}
      emptyText={text.noDeletable}
      submitLabel={text.deleteConfirm}
      submitLabelMany={text.deleteConfirmMany}
      warning={text.deleteWarning}
      submit={(token, item) => deleteCampaignRequestAPI(token, item.id)}
    />
  );
}

export function DeletedCampaignsModal({ stores, onClose }) {
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  return (
    <StoreCampaignModal
      mode="deleted"
      stores={stores}
      onClose={onClose}
      onDone={() => {}}
      title={text.deletedTitle}
      icon={<Archive size={20} aria-hidden="true" />}
      status="deleted"
      emptyText={text.noDeleted}
    />
  );
}
